import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';

/** While an inline quick sign-up (email → phone OTP) is mid-flight on a public
 *  page, the session already exists but the visitor must NOT be bounced into
 *  /app yet — the dialog finishes the flow and navigates explicitly. */
const HOLD_KEY = 'founderos:hold-portal-bounce';
export const holdPortalBounce = (on) => { try { if (on) sessionStorage.setItem(HOLD_KEY, '1'); else sessionStorage.removeItem(HOLD_KEY); } catch { /* ignore */ } };
const bounceHeld = () => { try { return sessionStorage.getItem(HOLD_KEY) === '1'; } catch { return false; } };

/**
 * Route wrapper that keeps public URLs (`/e/:id`, `/v/:slug`, `/founder/:slug`,
 * `/discover/:slug`) working for anonymous visitors while transparently
 * shuttling signed-in users to their in-portal equivalent.
 *
 * Anonymous visitor to `/e/abc123` sees the public RSVP page. A signed-in
 * founder tapping the same link is bounced to `/app/events/abc123` where
 * they can RSVP inline without re-entering their name/email.
 *
 * Usage:
 *   <Route path="/e/:eventId" element={
 *     <PublicOrPortal portalPathBuilder={(p) => `/app/events/${p.eventId}`}>
 *       <PublicEvent />
 *     </PublicOrPortal>
 *   } />
 *
 * `portalPathBuilder` receives `useParams()` output. Return `null` to skip
 * the redirect (i.e. even if signed in, stay on the public page).
 */
export default function PublicOrPortal({ portalPathBuilder, children }) {
  const { user, loading } = useAuth();
  const params = useParams();
  const location = useLocation();

  // While `/api/auth/me` is revalidating on hard reload we don't yet know if
  // this is a logged-in visitor. Render the public page in the meantime —
  // if it turns out they were signed in we'll flip once the auth resolves.
  if (loading) return children;

  if (user && portalPathBuilder && !bounceHeld()) {
    const target = portalPathBuilder(params);
    if (target && target !== location.pathname) {
      return <Navigate to={target + location.search + location.hash} replace />;
    }
  }
  return children;
}
